<script lang="ts">
/** An invite needs the person's first and last name; true when either is blank. */
export const missingName = (row: { first_name?: string; last_name?: string }) =>
  !row.first_name?.trim() || !row.last_name?.trim()
</script>

<script setup lang="ts">
import { Trash2 } from 'lucide-vue-next'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import { MemberAvatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export interface InviteRowItem {
  member_id: number | null
  first_name?: string
  last_name?: string
  email: string | null
  phone?: string | null
  avatar_url: string | null
  display_name: string | null
  role: string | number
  [key: string]: any
}

export interface RoleOption {
  value: string | number
  label: string
  disabled?: boolean
}

const props = defineProps<{
  rows: InviteRowItem[]
  roles: RoleOption[]
  requireNames?: boolean
}>()

const emit = defineEmits<{
  remove: [index: number]
  'update:role': [index: number, role: string | number]
}>()

const needsName = (row: InviteRowItem) => !!props.requireNames && (!row.name_editing ? missingName(row) : true)

const editName = (row: InviteRowItem, field: 'first_name' | 'last_name', value: string | number) => {
  row.name_editing = true
  row[field] = String(value)
  row.display_name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || null
}

const displayName = (row: InviteRowItem) => {
  return row.display_name
    || `${row.first_name || ''} ${row.last_name || ''}`.trim()
    || row.email
    || row.phone
    || 'Unknown'
}
</script>

<template>
  <div v-if="rows.length > 0" class="border rounded-md divide-y">
    <div
      v-for="(row, index) in rows"
      :key="row.email || row.phone || index"
      class="px-3 py-2"
    >
      <div class="flex items-center gap-3">
        <MemberAvatar :avatar_url="row.avatar_url" :verified="row.verified" />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="truncate text-sm font-medium flex items-center gap-1">
            {{ displayName(row) }}
            <VerifiedMarker v-if="row.verified" class="shrink-0" />
          </span>
          <span v-if="row.email || row.phone" class="truncate text-xs text-muted-foreground">
            {{ row.email || row.phone }}
          </span>
        </div>
        <slot name="row-trailing" :row="row" :index="index">
          <Select
            :model-value="row.role"
            @update:model-value="v => emit('update:role', index, v as string | number)"
          >
            <SelectTrigger class="h-7 w-auto gap-1 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="r in roles" :key="r.value" :value="r.value" :disabled="r.disabled" class="text-xs">
                {{ r.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </slot>
        <Button variant="ghost" size="sm" class="h-6 w-6 p-0 shrink-0" @click="emit('remove', index)">
          <Trash2 class="h-4 w-4 text-red-400" />
        </Button>
      </div>
      <div v-if="needsName(row)" class="mt-2 flex gap-2 pl-11" data-testid="invite-row-names">
        <Input
          :model-value="row.first_name || ''"
          placeholder="First name"
          class="h-8 text-xs"
          :class="{ 'border-destructive': !row.first_name?.trim() }"
          @update:model-value="v => editName(row, 'first_name', v)"
        />
        <Input
          :model-value="row.last_name || ''"
          placeholder="Last name"
          class="h-8 text-xs"
          :class="{ 'border-destructive': !row.last_name?.trim() }"
          @update:model-value="v => editName(row, 'last_name', v)"
        />
      </div>
      <slot name="row-extra" :row="row" :index="index" />
    </div>
  </div>
</template>
